import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { Clock } from 'lucide-react';
import {
  parseDate,
  daysBetween,
  addDays,
  formatDateShort,
  toDateStr,
  STATUS_BAR,
  STATUS_DOT,
} from '@/components/gantt/ganttUtils';
import GanttSummaryRow from '@/components/gantt/GanttSummaryRow';

// ---------- main ----------
const PhaseGanttChart = ({
  project,
  phases,
  sortedPhases,
  expandedPhase,
  setExpandedPhase,
  isMobile,
  formatCurrency,
  onUpdateDates,
  bomFulfillmentMap = {},
}) => {
  const scrollRef = useRef(null);
  const headerScrollRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const wrapperRef = useRef(null);

  // Drag state for resize/move
  // { phaseId, mode: 'move'|'resize-left'|'resize-right', startX, origStart: Date, origEnd: Date, newStart: Date, newEnd: Date, moved: bool }
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);

  // Track wrapper width (to stretch chart to fill viewport when project is short)
  useEffect(() => {
    const update = () => {
      if (wrapperRef.current) setContainerWidth(wrapperRef.current.clientWidth);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const LEFT_PANEL_WIDTH = isMobile ? 140 : 200;
  const ROW_HEIGHT = 56;
  const HEADER_HEIGHT = 40;
  const BAR_HEIGHT = 32;

  // Global range: use project dates when set; else derive from phase dates
  const { globalStart, globalEnd, totalDays } = useMemo(() => {
    let start = parseDate(project?.start_date);
    let end = parseDate(project?.end_date);

    const phaseStarts = phases.map(p => parseDate(p.start_date)).filter(Boolean);
    const phaseEnds = phases.map(p => parseDate(p.end_date)).filter(Boolean);

    if (!start && phaseStarts.length > 0) start = new Date(Math.min(...phaseStarts));
    if (!end && phaseEnds.length > 0) end = new Date(Math.max(...phaseEnds));

    if (!start) start = new Date();
    if (!end) end = addDays(start, 90);
    if (end <= start) end = addDays(start, 30);

    // 3-day padding on each side for breathing room
    const gs = addDays(start, -3);
    const ge = addDays(end, 3);
    return { globalStart: gs, globalEnd: ge, totalDays: Math.max(daysBetween(gs, ge), 1) };
  }, [project, phases]);

  // Auto zoom: choose day width based on project span + device
  const { dayWidth, markerMode } = useMemo(() => {
    const months = totalDays / 30;
    if (months < 3) {
      return { dayWidth: isMobile ? 14 : 24, markerMode: 'week' };
    } else if (months <= 6) {
      return { dayWidth: isMobile ? 8 : 12, markerMode: 'month-week' };
    }
    return { dayWidth: isMobile ? 4 : 6, markerMode: 'month' };
  }, [totalDays, isMobile]);

  // Natural width from day count; stretch to fill available space if smaller
  const rightAvailable = Math.max(containerWidth - LEFT_PANEL_WIDTH, 200);
  const naturalWidth = totalDays * dayWidth;
  const timelineWidth = Math.max(naturalWidth, rightAvailable);
  const effectiveDayWidth = timelineWidth / totalDays;

  // Date markers for header
  const markers = useMemo(() => {
    const out = [];
    const d = new Date(globalStart);
    if (markerMode === 'week') {
      // Align to Monday
      const day = d.getDay();
      const offsetDays = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + offsetDays);
      while (d <= globalEnd) {
        const off = daysBetween(globalStart, d) * effectiveDayWidth;
        out.push({ date: new Date(d), offset: off, label: formatDateShort(d), major: true });
        d.setDate(d.getDate() + 7);
      }
    } else if (markerMode === 'month-week') {
      // Month markers (major) + week ticks (minor)
      // Majors
      const m = new Date(globalStart);
      m.setDate(1);
      if (m < globalStart) m.setMonth(m.getMonth() + 1);
      while (m <= globalEnd) {
        const off = daysBetween(globalStart, m) * effectiveDayWidth;
        out.push({
          date: new Date(m),
          offset: off,
          label: m.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          major: true,
        });
        m.setMonth(m.getMonth() + 1);
      }
      // Minors (weeks)
      const w = new Date(globalStart);
      const wday = w.getDay();
      const wOffset = wday === 0 ? -6 : 1 - wday;
      w.setDate(w.getDate() + wOffset);
      while (w <= globalEnd) {
        const off = daysBetween(globalStart, w) * effectiveDayWidth;
        out.push({ date: new Date(w), offset: off, label: '', major: false });
        w.setDate(w.getDate() + 7);
      }
    } else {
      const m = new Date(globalStart);
      m.setDate(1);
      if (m < globalStart) m.setMonth(m.getMonth() + 1);
      while (m <= globalEnd) {
        const off = daysBetween(globalStart, m) * effectiveDayWidth;
        out.push({
          date: new Date(m),
          offset: off,
          label: m.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          major: true,
        });
        m.setMonth(m.getMonth() + 1);
      }
    }
    return out;
  }, [globalStart, globalEnd, markerMode, effectiveDayWidth]);

  // Today
  const today = useMemo(() => new Date(), []);
  const todayVisible = today >= globalStart && today <= globalEnd;
  const todayOffset = daysBetween(globalStart, today) * effectiveDayWidth;

  // Scroll to Today on mount / when width changes
  useEffect(() => {
    if (!scrollRef.current || !todayVisible) return;
    const cw = scrollRef.current.clientWidth;
    scrollRef.current.scrollLeft = Math.max(0, todayOffset - cw / 3);
  }, [todayVisible, todayOffset]);

  const handleScroll = useCallback((e) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  }, []);

  const barPos = (startStr, endStr) => {
    const s = parseDate(startStr);
    const e = parseDate(endStr);
    if (!s || !e) return null;
    const left = daysBetween(globalStart, s) * effectiveDayWidth;
    const width = Math.max(daysBetween(s, e) * effectiveDayWidth, 8);
    return { left, width };
  };

  // Progress % for budget bar inside each row
  const budgetPct = (phase) => {
    if (!phase.budget || phase.budget <= 0) return 0;
    return Math.min((phase.spent_amount / phase.budget) * 100, 100);
  };

  const budgetColor = (pct) =>
    pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-green-500';

  // Summary metrics
  const totalPhases = phases.length;
  const completedCount = phases.filter(p => p.status === 'Complete').length;
  const totalBudget = phases.reduce((s, p) => s + (p.budget || 0), 0);
  const totalSpent = phases.reduce((s, p) => s + (p.spent_amount || 0), 0);

  // Time elapsed % based on project range
  const timeElapsedPct = useMemo(() => {
    const projStart = parseDate(project?.start_date);
    const projEnd = parseDate(project?.end_date);
    if (!projStart || !projEnd || projEnd <= projStart) return null;
    const total = daysBetween(projStart, projEnd);
    const elapsed = daysBetween(projStart, today);
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  }, [project, today]);

  // ---------- Drag to resize/move ----------
  const canEdit = typeof onUpdateDates === 'function';

  const computeNewDates = useCallback((d, deltaDays) => {
    if (d.mode === 'move') {
      return {
        newStart: addDays(d.origStart, deltaDays),
        newEnd: addDays(d.origEnd, deltaDays),
      };
    }
    if (d.mode === 'resize-left') {
      let ns = addDays(d.origStart, deltaDays);
      // clamp: start must be <= end - 1 day
      const maxStart = addDays(d.origEnd, -1);
      if (ns > maxStart) ns = maxStart;
      return { newStart: ns, newEnd: d.origEnd };
    }
    // resize-right
    let ne = addDays(d.origEnd, deltaDays);
    const minEnd = addDays(d.origStart, 1);
    if (ne < minEnd) ne = minEnd;
    return { newStart: d.origStart, newEnd: ne };
  }, []);

  useEffect(() => {
    if (!drag) return;

    const SNAP_THRESHOLD = 3; // days

    const nearestSnap = (d) => {
      // Nearest Monday
      const day = d.getDay(); // 0 Sun .. 6 Sat
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const monday = addDays(d, diffToMonday);
      const mondayDelta = Math.abs(daysBetween(d, monday));

      // Nearest month-start
      const firstThis = new Date(d); firstThis.setDate(1);
      const firstNext = new Date(d); firstNext.setMonth(firstNext.getMonth() + 1); firstNext.setDate(1);
      const thisDelta = Math.abs(daysBetween(d, firstThis));
      const nextDelta = Math.abs(daysBetween(d, firstNext));
      const month = thisDelta <= nextDelta ? firstThis : firstNext;
      const monthDelta = Math.min(thisDelta, nextDelta);

      // Prefer month when both within threshold (monthly alignment is stronger signal)
      if (monthDelta <= SNAP_THRESHOLD && monthDelta <= mondayDelta) {
        return { date: month, kind: 'month' };
      }
      if (mondayDelta <= SNAP_THRESHOLD) {
        return { date: monday, kind: 'week' };
      }
      return null;
    };

    const applySnap = (raw) => {
      let snappedStart = raw.newStart;
      let snappedEnd = raw.newEnd;
      let snapGuide = null;

      if (drag.mode === 'resize-left') {
        const s = nearestSnap(raw.newStart);
        if (s) {
          // Respect clamp: must keep start < end
          const maxStart = addDays(drag.origEnd, -1);
          snappedStart = s.date > maxStart ? maxStart : s.date;
          snapGuide = { ...s, date: snappedStart };
        }
      } else if (drag.mode === 'resize-right') {
        const s = nearestSnap(raw.newEnd);
        if (s) {
          const minEnd = addDays(drag.origStart, 1);
          snappedEnd = s.date < minEnd ? minEnd : s.date;
          snapGuide = { ...s, date: snappedEnd };
        }
      } else {
        // move: snap whichever edge is closer to a boundary
        const sS = nearestSnap(raw.newStart);
        const sE = nearestSnap(raw.newEnd);
        let useStart = null;
        if (sS && sE) {
          const dS = Math.abs(daysBetween(raw.newStart, sS.date));
          const dE = Math.abs(daysBetween(raw.newEnd, sE.date));
          useStart = dS <= dE;
        } else if (sS) useStart = true;
        else if (sE) useStart = false;

        if (useStart === true) {
          const delta = daysBetween(raw.newStart, sS.date);
          snappedStart = addDays(raw.newStart, delta);
          snappedEnd = addDays(raw.newEnd, delta);
          snapGuide = { ...sS, date: snappedStart };
        } else if (useStart === false) {
          const delta = daysBetween(raw.newEnd, sE.date);
          snappedStart = addDays(raw.newStart, delta);
          snappedEnd = addDays(raw.newEnd, delta);
          snapGuide = { ...sE, date: snappedEnd };
        }
      }
      return { snappedStart, snappedEnd, snapGuide };
    };

    const handleMove = (e) => {
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      if (clientX == null) return;
      const deltaX = clientX - drag.startX;
      const deltaDays = Math.round(deltaX / effectiveDayWidth);
      const raw = computeNewDates(drag, deltaDays);
      const { snappedStart, snappedEnd, snapGuide } = applySnap(raw);
      const moved = deltaDays !== 0 || drag.moved;
      dragRef.current = {
        ...drag,
        newStart: snappedStart,
        newEnd: snappedEnd,
        snapGuide,
        moved,
      };
      setDrag(dragRef.current);
    };

    const handleUp = () => {
      const d = dragRef.current;
      if (d && d.moved) {
        const nsStr = toDateStr(d.newStart);
        const neStr = toDateStr(d.newEnd);
        const origStartStr = toDateStr(d.origStart);
        const origEndStr = toDateStr(d.origEnd);
        if (nsStr !== origStartStr || neStr !== origEndStr) {
          onUpdateDates(d.phaseId, nsStr, neStr);
        }
      }
      dragRef.current = null;
      setDrag(null);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [drag, effectiveDayWidth, computeNewDates, onUpdateDates]);

  const startDrag = (e, phase, mode) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    const origStart = parseDate(phase.start_date);
    const origEnd = parseDate(phase.end_date);
    if (!origStart || !origEnd) return;
    const initial = {
      phaseId: phase.phase_id,
      mode,
      startX: e.clientX,
      origStart,
      origEnd,
      newStart: origStart,
      newEnd: origEnd,
      moved: false,
    };
    dragRef.current = initial;
    setDrag(initial);
  };

  return (
    <div ref={wrapperRef} className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="phase-gantt-chart">
      <div className="flex">
        {/* ===== LEFT STICKY PANEL ===== */}
        <div
          className="flex-shrink-0 border-r-2 border-black bg-white"
          style={{ width: LEFT_PANEL_WIDTH }}
        >
          {/* Header */}
          <div
            className="border-b-2 border-black bg-zinc-100 px-3 flex items-center"
            style={{ height: HEADER_HEIGHT }}
          >
            <span className="text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-600">
              Phase
            </span>
          </div>

          {/* Phase labels */}
          {sortedPhases.map((phase) => {
            const isSel = expandedPhase === phase.phase_id;
            return (
              <button
                key={`label-${phase.phase_id}`}
                onClick={() => setExpandedPhase(isSel ? null : phase.phase_id)}
                data-testid={`gantt-phase-label-${phase.phase_id}`}
                className={`w-full flex items-center gap-2 px-3 border-b border-zinc-200 text-left hover:bg-zinc-50 transition-colors ${
                  isSel ? 'bg-yellow-50' : ''
                }`}
                style={{ height: ROW_HEIGHT }}
              >
                <div
                  className={`w-2.5 h-2.5 flex-shrink-0 ${STATUS_DOT[phase.status] || 'bg-zinc-400'}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold uppercase tracking-wide text-zinc-900 text-xs sm:text-sm truncate">
                    {phase.name}
                  </p>
                  <p className="text-[10px] font-mono text-zinc-500 truncate">
                    {phase.status}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ===== RIGHT SCROLLABLE AREA ===== */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Header (date axis) */}
          <div
            ref={headerScrollRef}
            className="overflow-hidden bg-zinc-100 border-b-2 border-black relative"
            style={{ height: HEADER_HEIGHT }}
          >
            <div className="relative" style={{ width: timelineWidth, height: HEADER_HEIGHT }}>
              {markers.map((m, i) => (
                <div
                  key={`mkr-${i}`}
                  className={`absolute top-0 h-full flex items-center ${
                    m.major ? 'border-l border-zinc-400' : 'border-l border-zinc-300'
                  }`}
                  style={{ left: m.offset }}
                >
                  {m.label && (
                    <span className="pl-1.5 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-700 whitespace-nowrap">
                      {m.label}
                    </span>
                  )}
                </div>
              ))}
              {/* Today marker */}
              {todayVisible && (
                <div
                  className="absolute top-0 h-full w-0.5 bg-orange-500 z-20"
                  style={{ left: todayOffset }}
                >
                  <div className="absolute -top-0 left-0 -translate-x-1/2 bg-orange-500 px-1.5 py-0.5 whitespace-nowrap">
                    <span className="text-[9px] font-heading font-bold uppercase tracking-wider text-white">
                      Today
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scrollable body */}
          <div
            ref={scrollRef}
            className="overflow-x-auto overflow-y-hidden"
            onScroll={handleScroll}
          >
            <div
              className="relative"
              style={{
                width: timelineWidth,
                height: sortedPhases.length * ROW_HEIGHT,
              }}
            >
              {/* Grid lines */}
              {markers.filter(m => m.major).map((m, i) => (
                <div
                  key={`grid-${i}`}
                  className="absolute top-0 bottom-0 border-l border-zinc-100"
                  style={{ left: m.offset }}
                />
              ))}

              {/* Today dashed line through body */}
              {todayVisible && (
                <div
                  className="absolute top-0 bottom-0 z-10 pointer-events-none"
                  style={{
                    left: todayOffset,
                    width: 0,
                    borderLeft: '2px dashed rgb(249 115 22)',
                  }}
                />
              )}

              {/* Snap guide line (visible during drag when edge snaps to week/month) */}
              {drag && drag.snapGuide && (() => {
                const off = daysBetween(globalStart, drag.snapGuide.date) * effectiveDayWidth;
                const isMonth = drag.snapGuide.kind === 'month';
                return (
                  <div
                    className="absolute top-0 bottom-0 z-30 pointer-events-none"
                    style={{
                      left: off,
                      width: 0,
                      borderLeft: `2px dashed ${isMonth ? 'rgb(249 115 22)' : 'rgb(255 196 12)'}`,
                    }}
                    data-testid="gantt-snap-guide"
                  >
                    <div
                      className={`absolute top-1 left-0 -translate-x-1/2 px-1.5 py-0.5 whitespace-nowrap border border-black shadow-[1px_1px_0px_0px_rgba(0,0,0,0.6)] ${
                        isMonth ? 'bg-orange-500 text-white' : 'bg-[#FFC40C] text-black'
                      }`}
                    >
                      <span className="text-[9px] font-heading font-bold uppercase tracking-wider">
                        {isMonth ? 'Month' : 'Week'}
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* Rows + bars */}
              {sortedPhases.map((phase, idx) => {
                const y = idx * ROW_HEIGHT;
                const isSel = expandedPhase === phase.phase_id;

                // Use drag-preview dates if this phase is currently being dragged
                const isDragging = drag && drag.phaseId === phase.phase_id;
                const effStart = isDragging ? toDateStr(drag.newStart) : phase.start_date;
                const effEnd = isDragging ? toDateStr(drag.newEnd) : phase.end_date;

                const bar = barPos(effStart, effEnd);
                const barClass = STATUS_BAR[phase.status] || 'bg-zinc-300';
                const pct = budgetPct(phase);

                return (
                  <div
                    key={`row-${phase.phase_id}`}
                    className={`absolute left-0 right-0 border-b border-zinc-200 ${
                      isSel ? 'bg-yellow-50' : ''
                    }`}
                    style={{ top: y, height: ROW_HEIGHT, width: timelineWidth }}
                  >
                    {/* Dates not set placeholder */}
                    {!bar && (
                      <div
                        className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-zinc-500"
                      >
                        <Clock className="w-3.5 h-3.5" />
                        <span className="text-[11px] font-heading font-semibold uppercase tracking-wider">
                          Dates not set
                        </span>
                      </div>
                    )}

                    {bar && (
                      <div
                        className="absolute"
                        style={{
                          top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                          left: bar.left,
                          width: bar.width,
                          height: BAR_HEIGHT,
                        }}
                        data-testid={`gantt-phase-bar-${phase.phase_id}`}
                      >
                        {/* Clickable bar body (also draggable for "move" if editable) */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            if (isDragging && drag.moved) return;
                            setExpandedPhase(isSel ? null : phase.phase_id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') setExpandedPhase(isSel ? null : phase.phase_id);
                          }}
                          onPointerDown={(e) => {
                            if (!canEdit) return;
                            // Only primary button & skip if on a resize handle (they stopPropagation)
                            if (e.button !== undefined && e.button !== 0) return;
                            startDrag(e, phase, 'move');
                          }}
                          className={`relative w-full h-full ${barClass} border-2 ${
                            isSel ? 'border-[#FFC40C]' : 'border-black'
                          } shadow-[2px_2px_0px_0px_rgba(0,0,0,0.6)] overflow-hidden transition-transform hover:-translate-y-0.5 ${
                            canEdit ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-pointer'
                          } select-none touch-none`}
                          data-testid={`gantt-phase-bar-body-${phase.phase_id}`}
                        >
                          {/* Label inside bar */}
                          {bar.width > 80 && (
                            <div className="absolute inset-0 flex items-center justify-between px-2 overflow-hidden pointer-events-none">
                              <span
                                className="text-[10px] sm:text-[11px] font-heading font-bold uppercase tracking-wider text-white truncate"
                                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                              >
                                {phase.name}
                              </span>
                              {bar.width > 160 && (
                                <span
                                  className="text-[9px] font-mono text-white whitespace-nowrap pl-2 hidden sm:inline"
                                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                                >
                                  {formatDateShort(parseDate(effStart))} →{' '}
                                  {formatDateShort(parseDate(effEnd))}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Budget indicator at the bottom of bar */}
                          {phase.budget > 0 && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30 pointer-events-none">
                              <div
                                className={`h-full ${budgetColor(pct)}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          )}

                          {/* BOM fulfillment indicator */}
                          {(() => {
                            const bom = bomFulfillmentMap[phase.phase_id];
                            if (!bom || bom.total === 0) return null;
                            const phaseStart = parseDate(phase.start_date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const daysToStart = phaseStart ? daysBetween(today, phaseStart) : null;
                            const isUrgent = phaseStart
                              && daysToStart >= 0
                              && daysToStart <= 14
                              && bom.fulfilled < bom.total;
                            let dotClass = 'bg-zinc-300';
                            if (bom.fulfillment_pct >= 100) dotClass = 'bg-emerald-400';
                            else if (isUrgent && bom.fulfilled === 0) dotClass = 'bg-red-500 animate-pulse';
                            else if (bom.fulfillment_pct > 0) dotClass = 'bg-yellow-400';
                            return (
                              <div
                                className={`absolute top-1 right-1 w-3 h-3 rounded-full border border-black/40 ${dotClass} pointer-events-none`}
                                title={`Materials: ${bom.fulfilled}/${bom.total} fulfilled (${bom.fulfillment_pct}%)`}
                                data-testid={`gantt-bom-dot-${phase.phase_id}`}
                              />
                            );
                          })()}
                        </div>

                        {/* Resize handles (only when editable) */}
                        {canEdit && (
                          <>
                            <div
                              onPointerDown={(e) => startDrag(e, phase, 'resize-left')}
                              data-testid={`gantt-phase-resize-left-${phase.phase_id}`}
                              className="absolute top-0 left-0 h-full w-2 sm:w-1.5 cursor-ew-resize z-10 touch-none flex items-center justify-center group/handle"
                              title="Drag to change start date"
                            >
                              <div className="w-0.5 h-3 bg-white/80 group-hover/handle:bg-[#FFC40C]" />
                            </div>
                            <div
                              onPointerDown={(e) => startDrag(e, phase, 'resize-right')}
                              data-testid={`gantt-phase-resize-right-${phase.phase_id}`}
                              className="absolute top-0 right-0 h-full w-2 sm:w-1.5 cursor-ew-resize z-10 touch-none flex items-center justify-center group/handle"
                              title="Drag to change end date"
                            >
                              <div className="w-0.5 h-3 bg-white/80 group-hover/handle:bg-[#FFC40C]" />
                            </div>
                          </>
                        )}

                        {/* Drag tooltip showing live dates */}
                        {isDragging && (
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white px-2 py-0.5 text-[10px] font-mono whitespace-nowrap z-30 pointer-events-none">
                            {formatDateShort(drag.newStart)} → {formatDateShort(drag.newEnd)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <GanttSummaryRow
        totalPhases={totalPhases}
        completedCount={completedCount}
        timeElapsedPct={timeElapsedPct}
        totalSpent={totalSpent}
        totalBudget={totalBudget}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default PhaseGanttChart;
